import {
	ArrowRight,
	CheckCircle,
	ChevronDown,
	Clock,
	Package,
} from "lucide-react";
import type React from "react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Badge } from "../components/ui/badge";
import { Card } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import { useAuth } from "../context/AuthContext";
import { useWallets } from "../context/WalletContext";
import {
	getIntegrationBadgeVariant,
	getOnboardingBadgeVariant,
} from "../lib/statusBadge";
import { cn } from "../lib/utils";
import type { Wallet } from "../types";

const INTEGRATION_STATUSES = [
	"New - Pre-Integration",
	"Integrating",
	"Testing",
	"Ready to Go Live",
	"Live",
];

const ONBOARDING_STATUSES = [
	"Not Started",
	"Onboarding in Progress",
	"Onboarded",
];

function getIntegrationProgress(wallet: Wallet) {
	const currentIndex = INTEGRATION_STATUSES.indexOf(wallet.integration_status);
	const completed = currentIndex >= 0 ? currentIndex : 0;
	return { completed, total: INTEGRATION_STATUSES.length };
}

function getOnboardingProgress(wallet: Wallet) {
	const currentIndex = ONBOARDING_STATUSES.indexOf(wallet.onboarding_status);
	const completed = currentIndex >= 0 ? currentIndex : 0;
	return { completed, total: ONBOARDING_STATUSES.length };
}

const PartnerDashboard: React.FC = () => {
	const { user } = useAuth();
	const { wallets } = useWallets();
	const navigate = useNavigate();
	const [selectedIdx, setSelectedIdx] = useState(0);
	const [selectorOpen, setSelectorOpen] = useState(false);

	if (!wallets.length) {
		return (
			<div className="min-h-[calc(100vh-56px)] bg-(--color-background) px-4 pt-20 pb-8">
				<div className="mx-auto max-w-[1200px]">
					<Card className="mx-auto max-w-lg p-10 text-center">
						<div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-(--color-muted) text-(--color-muted-foreground)">
							<Package size={48} />
						</div>
						<h2 className="mb-3 font-semibold text-(--color-foreground) text-2xl">
							No Wallet Assigned
						</h2>
						<p className="mb-6 text-(--color-muted-foreground) leading-relaxed">
							You don't have access to any wallets yet. Please contact MoneyGram
							to get started.
						</p>
						<div className="rounded-(--radius-sm) border border-(--color-info-200) bg-(--color-info-50) p-4 text-left">
							<strong className="mb-1 block text-(--color-info-700)">
								Need Help?
							</strong>
							<p className="text-(--color-muted-foreground) text-sm">
								Contact your MoneyGram representative to get access to your
								wallet portal.
							</p>
						</div>
					</Card>
				</div>
			</div>
		);
	}

	const myWallet = wallets[selectedIdx] ?? wallets[0];
	const integrationProgress = getIntegrationProgress(myWallet);
	const onboardingProgress = getOnboardingProgress(myWallet);
	const integrationPct = Math.round(
		(integrationProgress.completed / integrationProgress.total) * 100,
	);
	const onboardingPct = Math.round(
		(onboardingProgress.completed / onboardingProgress.total) * 100,
	);

	return (
		<div className="min-h-[calc(100vh-56px)] bg-(--color-background) px-4 pt-20 pb-8 md:px-6">
			<div className="mx-auto max-w-[1200px]">
				<div className="mb-8">
					<h1 className="font-semibold text-(--color-foreground) text-2xl tracking-tight">
						Welcome back, {user?.name}
					</h1>

					{wallets.length > 1 ? (
						<div className="relative mt-2">
							<button
								type="button"
								onClick={() => setSelectorOpen(!selectorOpen)}
								className="flex items-center gap-2 rounded-(--radius-sm) border border-(--color-border) bg-(--color-card) px-3 py-1.5 text-(--color-foreground) text-sm transition-colors hover:bg-(--color-muted)"
							>
								<span className="font-medium">{myWallet.wallet_name}</span>
								<span className="text-(--color-muted-foreground)">•</span>
								<span className="text-(--color-muted-foreground)">
									{myWallet.company_name}
								</span>
								<ChevronDown
									size={14}
									className={cn(
										"ml-1 transition-transform",
										selectorOpen && "rotate-180",
									)}
								/>
							</button>
							{selectorOpen && (
								<div className="absolute z-10 mt-1 w-full max-w-sm rounded-(--radius-md) border border-(--color-border) bg-(--color-card) py-1 shadow-lg">
									{wallets.map((w, idx) => (
										<button
											key={w.id}
											type="button"
											onClick={() => {
												setSelectedIdx(idx);
												setSelectorOpen(false);
											}}
											className={cn(
												"flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-(--color-muted)",
												idx === selectedIdx && "bg-(--color-muted) font-medium",
											)}
										>
											<span>{w.wallet_name}</span>
											<span className="text-(--color-muted-foreground)">
												• {w.company_name}
											</span>
										</button>
									))}
								</div>
							)}
						</div>
					) : (
						<p className="mt-1 text-(--color-muted-foreground) text-sm">
							{myWallet.wallet_name} • {myWallet.company_name}
						</p>
					)}
				</div>

				<div className="grid grid-cols-1 gap-8 md:grid-cols-2">
					{/* Integration Card */}
					<Card
						className={cn(
							"group relative flex cursor-pointer flex-col overflow-hidden p-10 transition-all duration-300 hover:-translate-y-1 hover:shadow-md",
						)}
						onClick={() => navigate(`/wallet/${myWallet.id}`)}
						role="button"
						tabIndex={0}
						onKeyDown={(e) => {
							if (e.key === "Enter" || e.key === " ") {
								e.preventDefault();
								navigate(`/wallet/${myWallet.id}`);
							}
						}}
					>
						{/* Top accent bar shown on hover */}
						<div className="absolute top-0 right-0 left-0 h-1 bg-(--color-primary-500) opacity-0 transition-opacity duration-300 group-hover:opacity-100" />

						<div className="mb-6 flex items-center gap-4">
							{myWallet.integration_status === "Live" ? (
								<CheckCircle
									size={28}
									className="shrink-0 text-(--color-success-500)"
								/>
							) : (
								<Clock
									size={28}
									className="shrink-0 text-(--color-warning-500)"
								/>
							)}
							<h3 className="flex-1 font-semibold text-(--color-foreground) text-2xl">
								Integration
							</h3>
							<ArrowRight
								size={24}
								className="shrink-0 text-(--color-primary-500) transition-transform duration-300 group-hover:translate-x-1"
							/>
						</div>

						<Badge
							variant={getIntegrationBadgeVariant(myWallet.integration_status)}
							className="mb-6 self-start px-3 py-1 text-sm"
						>
							{myWallet.integration_status}
						</Badge>

						<Progress value={integrationPct} className="mb-3 h-3" />
						<span className="font-medium text-(--color-muted-foreground) text-sm">
							{integrationProgress.completed} of {integrationProgress.total}{" "}
							steps completed
						</span>
					</Card>

					{/* Onboarding Card */}
					<Card
						className="group relative flex cursor-pointer flex-col overflow-hidden p-10 transition-all duration-300 hover:-translate-y-1 hover:shadow-md"
						onClick={() => navigate(`/wallet/${myWallet.id}?tab=onboarding`)}
						role="button"
						tabIndex={0}
						onKeyDown={(e) => {
							if (e.key === "Enter" || e.key === " ") {
								e.preventDefault();
								navigate(`/wallet/${myWallet.id}?tab=onboarding`);
							}
						}}
					>
						<div className="absolute top-0 right-0 left-0 h-1 bg-(--color-primary-500) opacity-0 transition-opacity duration-300 group-hover:opacity-100" />

						<div className="mb-6 flex items-center gap-4">
							{myWallet.onboarding_status === "Onboarded" ? (
								<CheckCircle
									size={28}
									className="shrink-0 text-(--color-success-500)"
								/>
							) : (
								<Clock
									size={28}
									className="shrink-0 text-(--color-warning-500)"
								/>
							)}
							<h3 className="flex-1 font-semibold text-(--color-foreground) text-2xl">
								Onboarding
							</h3>
							<ArrowRight
								size={24}
								className="shrink-0 text-(--color-primary-500) transition-transform duration-300 group-hover:translate-x-1"
							/>
						</div>

						<Badge
							variant={getOnboardingBadgeVariant(myWallet.onboarding_status)}
							className="mb-6 self-start px-3 py-1 text-sm"
						>
							{myWallet.onboarding_status}
						</Badge>

						<Progress value={onboardingPct} className="mb-3 h-3" />
						<span className="font-medium text-(--color-muted-foreground) text-sm">
							{onboardingProgress.completed} of {onboardingProgress.total} steps
							completed
						</span>
					</Card>
				</div>

				{myWallet.slack_channel && (
					<div className="mt-8 rounded-(--radius-md) bg-(--color-primary-500) p-8 text-white shadow-md">
						<h3 className="mb-2 font-semibold text-lg">Need Assistance?</h3>
						<p className="mb-4 leading-relaxed opacity-95">
							Our team is here to help you through the integration and
							onboarding process
						</p>
						<div className="rounded-(--radius-sm) border border-white/20 bg-white/15 p-4 text-sm backdrop-blur-sm">
							<strong className="mb-1 block">Slack Channel:</strong>
							{myWallet.slack_channel}
						</div>
					</div>
				)}
			</div>
		</div>
	);
};

export default PartnerDashboard;
